import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import apiCall from "../../util/accounting/remote/apiCall";
import { setCompany } from "../../util/accounting/company";
import { INDUSTRY_LABELS } from "../../util/accounting/orgProfile";
import AppModal from "./AppModal";
import "./CompanyPicker.css";

/**
 * The doorway into accounting: which company's books are you opening?
 *
 * One firm here keeps the books of several — the contractor and the farm it
 * also owns — and each set lives in its own database. Nothing in accounting can
 * be shown until that choice is made, because a figure without a company behind
 * it is meaningless and a posting without one would land in the wrong ledger.
 * So this stands in front of every accounting page rather than being a setting
 * tucked away inside one.
 *
 * Each company gets its own card. The choice deserves the weight — opening the
 * wrong books is the one mistake here that is expensive to undo — and a card
 * carries what you need to tell them apart at a glance: the name, what the
 * company does, and the currency its figures are in.
 */

const CURRENCIES = ["UGX", "KES", "TZS", "RWF", "USD", "EUR", "GBP"];

const LOGO = `${process.env.PUBLIC_URL || ""}/template/assets/images/bericot_logo.png`;

/** Initials for the tile, so a set of companies is scannable at a glance. */
const initialsOf = (name = "") =>
  String(name)
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "—";

/** A stable tint per company, so each keeps the same colour on every visit. */
const TINTS = [
  "#5b5fd6", "#0e7c86", "#1c7a53", "#b06a12", "#a63d6b", "#6b4bbd", "#0f6aa8",
];
const tintFor = (id) => TINTS[Math.abs(Number(id) || 0) % TINTS.length];

export default function CompanyPicker() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    industry: "services",
    currency: "UGX",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setFailed("");
    const res = await apiCall("accounting/companies/list", {});
    if (res.status === "OK") setCompanies(res.details || []);
    else setFailed(res.message || "The list of companies could not be loaded.");
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = (company) => setCompany(company);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Give the company a name.");

    setSaving(true);
    // Creating a company builds it a whole new set of books, which takes a
    // moment — say so rather than leaving a silent button.
    const res = await apiCall("accounting/companies/create", {
      name: form.name.trim(),
      legal_name: form.legal_name.trim() || form.name.trim(),
      industry: form.industry,
      currency: form.currency,
    });
    setSaving(false);

    if (res.status !== "OK") {
      return toast.error(res.message || "The company could not be set up.");
    }
    toast.success(res.message || "Company set up.");
    setShowNew(false);
    // Open it straight away — you created it in order to work in it.
    open({ ...res.details, industry: form.industry, currency: form.currency });
    setForm({ name: "", legal_name: "", industry: "services", currency: "UGX" });
  };

  return (
    <div className="cp-screen">
      <Toaster position="top-center" />

      <div className="cp-card">
        {/* ── Brand side ──────────────────────────────────────────────── */}
        <div className="cp-brand">
          <div className="cp-brand-inner">
            <img className="cp-logo" src={LOGO} alt="Bericot" />

            <span className="cp-kicker">
              <i className="fa-solid fa-calculator" aria-hidden="true" />
              Accounting
            </span>
            <h2 className="cp-headline">One system, each company&rsquo;s own books</h2>
            <p className="cp-blurb">
              Every company you manage keeps its own ledger, its own financial
              years and its own reports. Nothing crosses between them.
            </p>
            <ul className="cp-points">
              <li>
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                Separate ledgers, separate trial balances
              </li>
              <li>
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                A chart of accounts shaped to what each company does
              </li>
              <li>
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                Reports printed under the right company&rsquo;s name
              </li>
            </ul>
          </div>

          <Link to="/" className="cp-back">
            <i className="fa-solid fa-arrow-left-long" aria-hidden="true" />
            Back to the main system
          </Link>
        </div>

        {/* ── Chooser side ────────────────────────────────────────────── */}
        <div className="cp-side">
          <h1 className="cp-title">Choose a company</h1>
          <p className="cp-sub">
            Everything you view and post belongs to the company you open.
            Leaving accounting closes it again.
          </p>

          <div className="cp-grid">
            {loading &&
              [0, 1, 2].map((i) => <div className="cp-skel" key={i} />)}

            {!loading && failed && (
              <div className="cp-state">
                <strong>{failed}</strong>
                The server may be unreachable.
                <br />
                <button className="cp-retry" onClick={load}>
                  Try again
                </button>
              </div>
            )}

            {!loading &&
              !failed &&
              companies.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="cp-company"
                  onClick={() => open(c)}
                >
                  <span
                    className="cp-tile"
                    style={{ background: tintFor(c.id) }}
                    aria-hidden="true"
                  >
                    {initialsOf(c.name)}
                  </span>

                  <span>
                    <span className="cp-cname" title={c.name}>
                      {c.name}
                    </span>
                    <span className="cp-cmeta">
                      {INDUSTRY_LABELS[c.industry] || c.industry}
                    </span>
                  </span>

                  <span className="cp-foot">
                    <span className="cp-chip">{c.currency || "UGX"}</span>
                    <span className="cp-open">
                      Open
                      <i className="fa-solid fa-arrow-right-long" aria-hidden="true" />
                    </span>
                  </span>
                </button>
              ))}

            {!loading && !failed && (
              <button
                type="button"
                className="cp-new"
                onClick={() => setShowNew(true)}
              >
                <i className="fa-solid fa-plus" aria-hidden="true" />
                {companies.length ? "New company" : "Set up the first company"}
              </button>
            )}
          </div>

          <p className="cp-note">
            A new company starts with an empty set of books and its own chart of
            accounts.
          </p>
        </div>
      </div>

      {showNew && (
        <AppModal
          show
          onClose={() => !saving && setShowNew(false)}
          title="Set up a new company"
          subtitle="It gets its own set of books, separate from every other company here."
          icon="fa-solid fa-building"
          busy={saving}
          footer={
            <>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setShowNew(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="new-company-form"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? "Building the books…" : "Create company"}
              </button>
            </>
          }
        >
          <form id="new-company-form" className="cp-form" onSubmit={create}>
            <div className="cp-field">
              <label htmlFor="cp-name">Company name</label>
              <input
                id="cp-name"
                autoFocus
                value={form.name}
                onChange={set("name")}
                placeholder="Bericot Africa Limited"
              />
            </div>

            <div className="cp-field">
              <label htmlFor="cp-legal">
                Registered name <span className="cp-opt">optional</span>
              </label>
              <input
                id="cp-legal"
                value={form.legal_name}
                onChange={set("legal_name")}
                placeholder="As it appears on statutory documents"
              />
            </div>

            <div className="cp-two">
              <div className="cp-field">
                <label htmlFor="cp-industry">What it does</label>
                <select
                  id="cp-industry"
                  value={form.industry}
                  onChange={set("industry")}
                >
                  {Object.entries(INDUSTRY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
                <span className="cp-hint">
                  Sets the dashboard, the menu and the chart of accounts it
                  starts with.
                </span>
              </div>
              <div className="cp-field">
                <label htmlFor="cp-currency">Reporting currency</label>
                <select
                  id="cp-currency"
                  value={form.currency}
                  onChange={set("currency")}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </form>
        </AppModal>
      )}
    </div>
  );
}
